import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useTheme } from '@mui/material/styles';
import {
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
  TextField,
  MenuItem,
  Chip,
  Button,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import deviceService from '../services/deviceService';

const barSegmentValueLabelsPlugin = {
  id: 'barSegmentValueLabelsV2',
  beforeDraw: (chart) => {
    if (chart.$_barSegmentValueLabelsCycleActive) return;
    chart.$_barSegmentValueLabelsCycleActive = true;
    chart.$_barSegmentValueLabelsDrawn = false;
    chart.$_barSegmentValueLabelsSeenKeys = new Set();
  },
  afterDraw: (chart) => {
    chart.$_barSegmentValueLabelsCycleActive = false;
  },
  afterDatasetsDraw: (chart) => {
    if (chart.$_barSegmentValueLabelsDrawn) return;
    chart.$_barSegmentValueLabelsDrawn = true;

    const opts = chart?.config?.options?.plugins?.[barSegmentValueLabelsPlugin.id] ?? chart?.options?.plugins?.[barSegmentValueLabelsPlugin.id];
    if (!opts || opts.display === false) return;

    const ctx = chart.ctx;
    const color = opts.color || '#333';
    const insideColor = opts.insideColor;
    const fontSize = opts.fontSize || 10;
    const fontFamily = opts.fontFamily;
    const minValue = Number.isFinite(opts.minValue) ? opts.minValue : 0;
    const positionByDatasetIndex = opts.positionByDatasetIndex;
    const formatter = typeof opts.formatter === 'function'
      ? opts.formatter
      : (v) => {
          const num = Number(v);
          if (!Number.isFinite(num)) return '';
          return Math.round(num).toLocaleString('en-US');
        };

    const drawDatasetLabels = (datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.hidden) return;
      if (meta.type !== 'bar') return;

      const dataset = chart.data?.datasets?.[datasetIndex];
      meta.data.forEach((element, index) => {
        const raw = dataset?.data?.[index];
        const value = Number(raw);
        if (!Number.isFinite(value)) return;
        if (value <= minValue) return;

        const pos = element.tooltipPosition ? element.tooltipPosition() : { x: element.x, y: element.y };
        const baseY = Number.isFinite(element?.base) ? element.base : pos.y;
        const topY = Math.min(pos.y, baseY);
        const bottomY = Math.max(pos.y, baseY);
        const midY = topY + ((bottomY - topY) / 2);
        const label = formatter(value, { datasetIndex, index, chart });
        if (!label) return;

        const seenKey = `${datasetIndex}:${index}:${label}:${Math.round(pos.x)}:${Math.round(pos.y)}`;
        if (chart.$_barSegmentValueLabelsSeenKeys?.has(seenKey)) return;
        chart.$_barSegmentValueLabelsSeenKeys?.add(seenKey);

        const position =
          positionByDatasetIndex && Object.prototype.hasOwnProperty.call(positionByDatasetIndex, datasetIndex)
            ? positionByDatasetIndex[datasetIndex]
            : 'middle';

        let y = midY;
        let baseline = 'middle';
        let fill = insideColor || color;

        if (position === 'insideTop') {
          const barHeight = bottomY - topY;
          if (barHeight < fontSize + 6) {
            y = topY - 4;
            baseline = 'bottom';
            fill = color;
          } else {
            y = topY + 2;
            baseline = 'top';
          }
        }

        ctx.save();
        ctx.fillStyle = fill;
        ctx.textAlign = 'center';
        ctx.textBaseline = baseline;
        ctx.font = `${fontSize}px ${fontFamily || 'sans-serif'}`;
        ctx.fillText(label, pos.x, y);
        ctx.restore();
      });
    };

    for (let i = 0; i < (chart.data?.datasets?.length || 0); i++) {
      drawDatasetLabels(i);
    }
  }
};

ChartJS.register(
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Enhanced Device Hierarchy tree with date range selection and usage calculation
 */
const DeviceFlowChart = ({
  deviceHierarchy,
  onDeviceSelect,
  parentOptions = [],
  parentOptionIds = [],
  parentAssignments = {},
  onParentAssignmentChange,
  canEditParents = false,
  parentAssignmentSaving = false,
  formatParentOptionLabel,
  signalThresholdReadings = 16
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  // Get current date and 30 days ago for default date range
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const formatUsageValue = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return n.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  const formatPercentValue = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return `${n.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })}%`;
  };

  const parentOptionIdSet = useMemo(() => {
    return parentOptionIds instanceof Set
      ? parentOptionIds
      : new Set((Array.isArray(parentOptionIds) ? parentOptionIds : []).map((id) => String(id)));
  }, [parentOptionIds]);

  const displayHierarchy = useMemo(() => {
    const sourceRoots = Array.isArray(deviceHierarchy) ? deviceHierarchy : [];
    if (sourceRoots.length === 0) return [];

    const nodeMap = new Map();
    const traverse = (node) => {
      if (!node || node.id === undefined || node.id === null) return;
      const id = String(node.id);
      if (nodeMap.has(id)) return;
      nodeMap.set(id, {
        ...node,
        id,
        parentId: node?.parentId !== undefined && node?.parentId !== null ? String(node.parentId) : '',
        children: []
      });
      (Array.isArray(node.children) ? node.children : []).forEach(traverse);
    };
    sourceRoots.forEach(traverse);

    const roots = [];
    for (const node of nodeMap.values()) {
      const id = String(node.id);
      const assignedParentRaw = Object.prototype.hasOwnProperty.call(parentAssignments || {}, id)
        ? parentAssignments[id]
        : undefined;
      const assignedParent = assignedParentRaw ? String(assignedParentRaw) : '';
      const fallbackParent = node?.parentId ? String(node.parentId) : '';
      const allowAssignmentOverride = assignedParentRaw !== undefined && (
        // Non-empty selections are explicit user choices.
        !!assignedParent
        // Clearing a bulk parent to "No parent" is also an explicit user choice.
        || parentOptionIdSet.has(fallbackParent)
        // For roots, keeping empty assignment should keep root behavior.
        || !fallbackParent
      );
      // parentAssignments tracks only bulk-parent selection, not full tree edges.
      // Keep original non-bulk parent chains unless the user made a real override.
      const effectiveParent = allowAssignmentOverride ? assignedParent : fallbackParent;
      const isBulkMeter = parentOptionIdSet.has(id);
      const parentId = isBulkMeter ? '' : effectiveParent;
      const parentNode = parentId ? nodeMap.get(parentId) : null;

      if (parentNode && parentNode.id !== id) {
        parentNode.children.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  }, [deviceHierarchy, parentAssignments, parentOptionIdSet]);

  const hasParentAssignmentOptions = Array.isArray(parentOptions) && parentOptions.length > 0;

  const formatParentLabel = (device) => {
    if (typeof formatParentOptionLabel === 'function') return formatParentOptionLabel(device);
    const serial = device?.serialNumber ? `S/N ${device.serialNumber}` : '';
    const description = device?.description || '';
    const locationText = device?.location || '';
    const main = serial && description ? `${serial} - ${description}` : (description || serial || String(device?.id || 'Device'));
    return locationText ? `${main} (${locationText})` : main;
  };
  
  // Format dates for input fields
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  // Format date for API request (YYYY-MM-DD)
  const formatDateForApi = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  
  // State for date range and usage calculation
  const [startDate, setStartDate] = useState(formatDateForInput(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(formatDateForInput(today));
  const [calculating, setCalculating] = useState(false);
  const [deviceUsage, setDeviceUsage] = useState({});
  const [deviceDailySignal, setDeviceDailySignal] = useState({});
  
  // Handle date change
  const handleStartDateChange = (e) => setStartDate(e.target.value);
  const handleEndDateChange = (e) => setEndDate(e.target.value);
  
  // State for error handling
  const [error, setError] = useState(null);

  // Local cache to store recent usage calculations and reduce API calls
  const usageCache = useRef({});
  // Cache timeout - 5 minutes
  const CACHE_TIMEOUT = 5 * 60 * 1000;

  const csvEscape = (value) => {
    if (value === null || value === undefined) return '';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const downloadCsvText = (filename, csvText) => {
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const buildDateKeysInRange = (start, end) => {
    const keys = [];
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || s > e) return keys;
    for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    return keys;
  };

  const buildSignalSummary = (deviceId) => {
    const counts = deviceDailySignal?.[deviceId];
    if (!counts) {
      return {
        readingsTotal: '',
        daysWithData: '',
        daysMissing: '',
        avgReadingsPerDay: '',
        minReadingsInDay: '',
        signalHealth: ''
      };
    }

    const dateKeys = buildDateKeysInRange(startDate, endDate);
    const readingsTotal = dateKeys.reduce((sum, k) => sum + (Number(counts[k]) || 0), 0);
    const daysWithData = dateKeys.filter((k) => (Number(counts[k]) || 0) > 0).length;
    const daysMissing = dateKeys.length - daysWithData;
    const avgReadingsPerDay = dateKeys.length ? readingsTotal / dateKeys.length : 0;
    const minReadingsInDay = dateKeys.length
      ? Math.min(...dateKeys.map((k) => Number(counts[k]) || 0))
      : 0;
    const threshold = Number.isFinite(Number(signalThresholdReadings))
      ? Number(signalThresholdReadings)
      : 16;
    const signalHealth = readingsTotal === 0
      ? 'No data'
      : (daysMissing > 0 || avgReadingsPerDay < threshold) ? 'Poor' : 'Good';

    return {
      readingsTotal,
      daysWithData,
      daysMissing,
      avgReadingsPerDay: Number(avgReadingsPerDay.toFixed(1)),
      minReadingsInDay,
      signalHealth
    };
  };

  const buildHierarchyExportRows = () => {
    const rows = [];

    const traverse = (node, level, parentId, pathParts) => {
      const label = node?.serialNumber || node?.description || node?.id;
      const nextPath = [...pathParts, label].filter(Boolean);
      const usage = Number(deviceUsage?.[node.id]);
      const hasUsage = Number.isFinite(usage);
      const children = Array.isArray(node.children) ? node.children : [];
      const childrenUsageSum = children.reduce((sum, c) => sum + (Number(deviceUsage?.[c.id]) || 0), 0);
      const diff = hasUsage ? usage - childrenUsageSum : '';
      const percentLoss = hasUsage && usage !== 0 ? (diff / usage) * 100 : '';
      const signal = buildSignalSummary(node.id);

      rows.push({
        level,
        path: nextPath.join(' > '),
        serialNumber: node.serialNumber || '',
        description: node.description || '',
        location: node.location || '',
        deviceType: node.deviceType || '',
        isActive: node.isActive === undefined || node.isActive === null ? '' : Boolean(node.isActive),
        isExternal: node.isExternal === undefined || node.isExternal === null ? '' : Boolean(node.isExternal),
        multiplier: node.multiplier ?? '',
        dailyThresholdLiters: node.dailyThresholdLiters ?? '',
        usage,
        childrenUsageSum,
        diff,
        percentLoss,
        readingsTotal: signal.readingsTotal,
        daysWithData: signal.daysWithData,
        daysMissing: signal.daysMissing,
        avgReadingsPerDay: signal.avgReadingsPerDay,
        minReadingsInDay: signal.minReadingsInDay,
        signalHealth: signal.signalHealth,
        startDate,
        endDate
      });

      children.forEach((child) => traverse(child, level + 1, node.id, nextPath));
    };

    (Array.isArray(displayHierarchy) ? displayHierarchy : []).forEach((root) => traverse(root, 0, '', []));
    return rows;
  };

  // Calculate usage for the selected date range
  const calculateUsage = async () => {
    setCalculating(true);
    setError(null);
    
    try {
      const usageData = {};
      const signalData = {};

      // Create a cache key based on date range
      const cacheKey = `${startDate}_${endDate}`;
      const now = Date.now();

      // Check if we have valid cached data for this date range
      if (usageCache.current[cacheKey] &&
          (now - usageCache.current[cacheKey].timestamp) < CACHE_TIMEOUT) {
        console.log('Using cached usage data for date range:', startDate, 'to', endDate);
        setDeviceUsage(usageCache.current[cacheKey].data);
        setDeviceDailySignal(usageCache.current[cacheKey].signal || {});
        setCalculating(false);
        return;
      }
      
      // Helper function to fetch device usage data recursively
      // Using a batch approach to reduce API calls
      const deviceBatches = [];
      const allDevices = [];
      
      // Collect all devices into a flat array
      const collectDevices = (device) => {
        allDevices.push(device);
        if (device.children && device.children.length > 0) {
          device.children.forEach(collectDevices);
        }
      };
      
      // Collect all devices first
      displayHierarchy.forEach(collectDevices);
      
      // Split into batches of 5 devices each to reduce concurrent requests
      const BATCH_SIZE = 5;
      for (let i = 0; i < allDevices.length; i += BATCH_SIZE) {
        deviceBatches.push(allDevices.slice(i, i + BATCH_SIZE));
      }
      
      console.log(`Processing ${allDevices.length} devices in ${deviceBatches.length} batches`);
      
      // Process batches sequentially to reduce server load
      for (const batch of deviceBatches) {
        await Promise.all(batch.map(async (device) => {
          try {
            // Fetch real usage data from the database
            const params = {
              startDate: formatDateForApi(startDate),
              endDate: formatDateForApi(endDate)
            };
            
            // Use deviceService.getDeviceDailyUsage which calls the existing endpoint
            const dailyUsageData = await deviceService.getDeviceDailyUsage(device.id, params);

            // Calculate total usage from daily data and apply multiplier
            const totalUsage = dailyUsageData.reduce((total, day) => {
              // Ensure we're looking at the correct property (totalUsage) and parsing it as a number
              const dayUsage = parseFloat(day.totalUsage || 0);
              return total + dayUsage;
            }, 0);

            // Keep per-day reading counts for the signal/health report in the CSV export
            const byDay = {};
            (Array.isArray(dailyUsageData) ? dailyUsageData : []).forEach((day) => {
              const dateKey = String(day?.date || '').slice(0, 10);
              if (!dateKey) return;
              byDay[dateKey] = (byDay[dateKey] || 0) + (Number(day.readingCount) || 0);
            });
            signalData[device.id] = byDay;

            usageData[device.id] = parseFloat((totalUsage).toFixed(2));
          } catch (err) {
            console.error(`Error fetching usage for device ${device.id}:`, err);
            // Still store a value to prevent UI issues
            usageData[device.id] = 0;
            signalData[device.id] = {};
          }
        }));
        
        // Add a small delay between batches to prevent overwhelming the server
        if (deviceBatches.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Cache the results
      usageCache.current[cacheKey] = {
        data: usageData,
        signal: signalData,
        timestamp: now
      };

      // Update state with actual usage data
      setDeviceUsage(usageData);
      setDeviceDailySignal(signalData);
    } catch (err) {
      console.error('Error calculating usage:', err);
      setError('Failed to fetch usage data. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  const handleExportHierarchyCsv = () => {
    const rows = buildHierarchyExportRows();
    const headers = [
      'level',
      'path',
      'serialNumber',
      'description',
      'location',
      'deviceType',
      'isActive',
      'isExternal',
      'multiplier',
      'dailyThresholdLiters',
      'usage',
      'childrenUsageSum',
      'diff',
      'percentLoss',
      'readingsTotal',
      'daysWithData',
      'daysMissing',
      'avgReadingsPerDay',
      'minReadingsInDay',
      'signalHealth',
      'startDate',
      'endDate'
    ];
    const safeStart = startDate || 'start';
    const safeEnd = endDate || 'end';

    const csvLines = [];
    csvLines.push(headers.map(csvEscape).join(','));
    rows.forEach((r) => {
      csvLines.push(headers.map((h) => csvEscape(r[h])).join(','));
    });

    // Health report: per-day signal (reading count) matrix for each meter,
    // appended as a second section in the same CSV.
    const dateKeys = buildDateKeysInRange(startDate, endDate);
    if (dateKeys.length > 0 && Object.keys(deviceDailySignal || {}).length > 0) {
      csvLines.push('');
      csvLines.push(csvEscape(`Signal per day (readings per meter) - threshold ${Number(signalThresholdReadings) || 16}/day`));
      csvLines.push(['serialNumber', 'description', 'path', ...dateKeys].map(csvEscape).join(','));

      const flat = [];
      const collect = (node, pathParts) => {
        const label = node?.serialNumber || node?.description || node?.id;
        const nextPath = [...pathParts, label].filter(Boolean);
        flat.push({ node, path: nextPath.join(' > ') });
        (Array.isArray(node?.children) ? node.children : []).forEach((child) => collect(child, nextPath));
      };
      (Array.isArray(displayHierarchy) ? displayHierarchy : []).forEach((root) => collect(root, []));

      flat.forEach(({ node, path }) => {
        const counts = deviceDailySignal?.[node.id] || {};
        csvLines.push(
          [
            node.serialNumber || '',
            node.description || '',
            path,
            ...dateKeys.map((k) => Number(counts[k]) || 0)
          ].map(csvEscape).join(',')
        );
      });
    }

    downloadCsvText(`device_hierarchy_${safeStart}_to_${safeEnd}.csv`, `﻿${csvLines.join('\r\n')}`);
  };

  const handleExportHierarchyXlsx = async () => {
    try {
      const { default: ExcelJS } = await import('exceljs');
      const rows = buildHierarchyExportRows();
      const dateKeys = buildDateKeysInRange(startDate, endDate);
      const threshold = Number(signalThresholdReadings) || 16;
      const workbook = new ExcelJS.Workbook();

      const styleHeader = (sheet) => {
        sheet.getRow(1).font = { bold: true };
        sheet.views = [{ state: 'frozen', ySplit: 1 }];
        sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: sheet.columnCount } };
      };
      const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

      const balance = workbook.addWorksheet('Water Balance');
      balance.columns = [
        { header: 'Level', key: 'level', width: 7 },
        { header: 'Path', key: 'path', width: 40 },
        { header: 'Serial Number', key: 'serialNumber', width: 16 },
        { header: 'Description', key: 'description', width: 28 },
        { header: 'Location', key: 'location', width: 20 },
        { header: 'Device Type', key: 'deviceType', width: 14 },
        { header: 'Active', key: 'isActive', width: 8 },
        { header: 'External', key: 'isExternal', width: 9 },
        { header: 'Multiplier', key: 'multiplier', width: 10 },
        { header: 'Daily Threshold (L)', key: 'dailyThresholdLiters', width: 18 },
        { header: 'Usage', key: 'usage', width: 12 },
        { header: 'Children Usage Sum', key: 'childrenUsageSum', width: 18 },
        { header: 'Difference', key: 'diff', width: 12 },
        { header: '% Loss', key: 'percentLoss', width: 9 },
        { header: 'Start Date', key: 'startDate', width: 12 },
        { header: 'End Date', key: 'endDate', width: 12 }
      ];
      rows.forEach((r) => {
        balance.addRow({
          ...r,
          usage: Number.isFinite(r.usage) ? r.usage : '',
          percentLoss: r.percentLoss === '' ? '' : Number(r.percentLoss.toFixed(2))
        }).getCell('path').alignment = { indent: r.level };
      });
      styleHeader(balance);

      const health = workbook.addWorksheet('Health Report');
      health.columns = [
        { header: 'Serial Number', key: 'serialNumber', width: 16 },
        { header: 'Description', key: 'description', width: 28 },
        { header: 'Location', key: 'location', width: 20 },
        { header: 'Path', key: 'path', width: 40 },
        { header: 'Total Readings', key: 'readingsTotal', width: 14 },
        { header: 'Days With Data', key: 'daysWithData', width: 14 },
        { header: 'Days Missing', key: 'daysMissing', width: 13 },
        { header: 'Avg Readings/Day', key: 'avgReadingsPerDay', width: 16 },
        { header: 'Min Readings/Day', key: 'minReadingsInDay', width: 16 },
        { header: 'Signal Health', key: 'signalHealth', width: 13 },
        ...dateKeys.map((k) => ({ header: k, key: k, width: 11 }))
      ];
      const firstDayCol = 11;
      const flat = [];
      const collect = (node, pathParts) => {
        const label = node?.serialNumber || node?.description || node?.id;
        const nextPath = [...pathParts, label].filter(Boolean);
        flat.push({ node, path: nextPath.join(' > ') });
        (Array.isArray(node?.children) ? node.children : []).forEach((child) => collect(child, nextPath));
      };
      (Array.isArray(displayHierarchy) ? displayHierarchy : []).forEach((root) => collect(root, []));

      flat.forEach(({ node, path }) => {
        const counts = deviceDailySignal?.[node.id] || {};
        const signal = buildSignalSummary(node.id);
        const row = health.addRow({
          serialNumber: node.serialNumber || '',
          description: node.description || '',
          location: node.location || '',
          path,
          ...signal,
          ...Object.fromEntries(dateKeys.map((k) => [k, Number(counts[k]) || 0]))
        });
        const healthColor = { Good: 'FFC6EFCE', Poor: 'FFFFEB9C', 'No data': 'FFFFC7CE' }[signal.signalHealth];
        if (healthColor) row.getCell('signalHealth').fill = fill(healthColor);
        if (!deviceDailySignal?.[node.id]) return;
        dateKeys.forEach((k, i) => {
          const value = Number(counts[k]) || 0;
          if (value < threshold) row.getCell(firstDayCol + i).fill = fill(value === 0 ? 'FFFFC7CE' : 'FFFFEB9C');
        });
      });
      styleHeader(health);
      health.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `water_balance_${startDate || 'start'}_to_${endDate || 'end'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      setError(`Failed to export Excel file${err && err.message ? `: ${err.message}` : ''}`);
    }
  };

  const sumLeafUsage = useCallback((node) => {
    if (!node) return 0;
    const children = Array.isArray(node.children) ? node.children : [];
    if (children.length === 0) {
      const v = Number(deviceUsage?.[node.id]);
      return Number.isFinite(v) ? v : 0;
    }
    return children.reduce((sum, c) => sum + sumLeafUsage(c), 0);
  }, [deviceUsage]);

  const rootNodes = useMemo(() => Array.isArray(displayHierarchy) ? displayHierarchy : [], [displayHierarchy]);

  const rootIdsWithChildren = useMemo(() => {
    return new Set(rootNodes.filter((n) => Array.isArray(n.children) && n.children.length > 0).map((n) => String(n.id)));
  }, [rootNodes]);

  const configuredBulkNodeIds = useMemo(() => {
    return new Set(rootNodes.filter((n) => parentOptionIdSet.has(String(n.id))).map((n) => String(n.id)));
  }, [rootNodes, parentOptionIdSet]);

  const bulkNodeIds = useMemo(() => {
    return configuredBulkNodeIds.size > 0 ? configuredBulkNodeIds : rootIdsWithChildren;
  }, [configuredBulkNodeIds, rootIdsWithChildren]);

  const bulkNodes = useMemo(() => {
    return rootNodes.filter((n) => bulkNodeIds.has(String(n.id)));
  }, [rootNodes, bulkNodeIds]);

  const unallocatedRoots = useMemo(() => {
    return rootNodes.filter((n) => !bulkNodeIds.has(String(n.id)));
  }, [rootNodes, bulkNodeIds]);

  const unallocatedTotal = useMemo(() => {
    return unallocatedRoots.reduce((sum, root) => sum + sumLeafUsage(root), 0);
  }, [unallocatedRoots, sumLeafUsage]);
  
  // Calculate the sum of all children's usage for a device
  const calculateChildrenUsageSum = (device) => {
    return sumLeafUsage(device);
  };
  
  // Function to render a device in the hierarchy
  const renderDevice = (device, level = 0) => {
    const hasChildren = device.children && device.children.length > 0;
    const deviceUsageValue = deviceUsage[device.id] || 0;
    const isExternal = Boolean(device?.isExternal);
    const showBreakdown = hasChildren && bulkNodeIds.has(String(device.id));
    
    // Determine the unit based on device type
    const isElectrical = device.deviceType === 'electrical_meter';
    const unit = isElectrical ? 'kWh' : 'L';
    
    // Calculate the sum of all children's usage and the difference (water loss or electrical loss)
    const childrenUsageSum = showBreakdown ? calculateChildrenUsageSum(device) : 0;
    const usageDifference = showBreakdown ? (deviceUsageValue - childrenUsageSum) : 0;
    const lossPercent = showBreakdown && deviceUsageValue !== 0 ? (usageDifference / deviceUsageValue) * 100 : NaN;
    const deviceId = device?.id !== undefined && device?.id !== null ? String(device.id) : '';
    const isBulkMeterRow = Boolean(deviceId) && bulkNodeIds.has(deviceId);
    const selectedParentId = deviceId
      ? (
          Object.prototype.hasOwnProperty.call(parentAssignments || {}, deviceId)
            ? (parentAssignments?.[deviceId] || '')
            : (device?.parentId ? String(device.parentId) : '')
        )
      : '';
    const showParentSelector = Boolean(deviceId) && !isExternal && hasParentAssignmentOptions && typeof onParentAssignmentChange === 'function';
    const handleStopRowClick = (event) => {
      event.stopPropagation();
    };
    
    return (
      <Box key={device.id} sx={{ pl: level * 2 }}>
        <ListItem 
          button 
          onClick={() => {
            if (onDeviceSelect) onDeviceSelect(device);
            navigate(`/devices/${device.id}`);
          }}
          sx={{
            borderLeft: isExternal ? '3px dashed #9e9e9e' : '3px solid #2196f3',
            bgcolor: 'background.paper',
            mb: 1,
            borderRadius: '4px',
            '&:hover': {
              bgcolor: isExternal ? 'rgba(158, 158, 158, 0.08)' : 'rgba(33, 150, 243, 0.08)'
            }
          }}
        >
          <ListItemText
            primary={
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: showParentSelector ? 'minmax(0, 1fr) 240px auto' : 'minmax(0, 1fr) auto'
                  },
                  alignItems: { xs: 'stretch', md: 'center' },
                  columnGap: 1,
                  rowGap: 1,
                  width: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ minWidth: 0 }}>
                    S/N: {device.serialNumber || '--'}{device.description ? ` — ${device.description}` : ''}
                    {device.location ? ` — ${device.location}` : ''}
                  </Typography>
                  {isExternal && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      External
                    </Typography>
                  )}
                </Box>

                {showParentSelector && (
                  <Box
                    sx={{ minWidth: { xs: '100%', md: 240 }, width: { xs: '100%', md: 240 }, flexShrink: 0 }}
                    onClick={handleStopRowClick}
                    onMouseDown={handleStopRowClick}
                  >
                    {isBulkMeterRow ? (
                      <Chip size="small" variant="outlined" color="primary" label="Bulk meter" />
                    ) : (
                      <TextField
                        select
                        size="small"
                        label="Parent"
                        value={selectedParentId}
                        onChange={(event) => {
                          handleStopRowClick(event);
                          onParentAssignmentChange(deviceId, event.target.value || '');
                        }}
                        onClick={handleStopRowClick}
                        onFocus={handleStopRowClick}
                        fullWidth
                        disabled={!canEditParents || parentAssignmentSaving}
                      >
                        <MenuItem value="">No parent</MenuItem>
                        {(parentOptions || [])
                          .filter((opt) => String(opt?.id) !== deviceId)
                          .map((opt) => (
                            <MenuItem key={String(opt.id)} value={String(opt.id)}>
                              {formatParentLabel(opt)}
                            </MenuItem>
                          ))}
                      </TextField>
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    ml: { xs: 0, md: 'auto' },
                    justifySelf: { xs: 'stretch', md: 'end' }
                  }}
                >
                  {deviceUsageValue !== undefined && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: isExternal ? '#616161' : '#1976d2',
                        bgcolor: isExternal ? 'rgba(97, 97, 97, 0.08)' : 'rgba(25, 118, 210, 0.1)',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        mb: hasChildren ? 0.5 : 0
                      }}
                    >
                      Usage: {formatUsageValue(deviceUsageValue)} {unit}
                    </Typography>
                  )}
                  
                  {showBreakdown && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.8rem',
                          color: '#4caf50',
                          bgcolor: 'rgba(76, 175, 80, 0.1)',
                          px: 1,
                          py: 0.3,
                          borderRadius: 1,
                          mb: 0.5
                        }}
                      >
                        Children: {formatUsageValue(childrenUsageSum)} {unit}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.8rem',
                          color: usageDifference < 0 ? '#f44336' : '#ff9800',
                          bgcolor: usageDifference < 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                          px: 1,
                          py: 0.3,
                          borderRadius: 1
                        }}
                      >
                        Diff: {formatUsageValue(usageDifference)} {unit}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8rem',
                          color: usageDifference < 0 ? '#f44336' : '#9c27b0',
                          bgcolor: usageDifference < 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(156, 39, 176, 0.1)',
                          px: 1,
                          py: 0.3,
                          borderRadius: 1,
                          mt: 0.5
                        }}
                      >
                        Loss: {formatPercentValue(lossPercent)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            }
          />
        </ListItem>
        
        {/* Render children recursively if they exist */}
        {hasChildren && (
          <Box sx={{ ml: 2, borderLeft: '1px dashed #bdbdbd', pl: 2 }}>
            {device.children.map(child => renderDevice(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  // If no device hierarchy data is available
  if (!displayHierarchy || displayHierarchy.length === 0) {
    return (
      <Card variant="outlined" sx={{ minHeight: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CardContent>
          <Typography variant="subtitle1" color="text.secondary" align="center">
            No device data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Main component render
  const usageKeysCount = Object.keys(deviceUsage || {}).length;
  const hasBulkSummary = bulkNodes.length > 0 && usageKeysCount > 0;
  const hasUnallocated = unallocatedRoots.length > 0 && usageKeysCount > 0;
  const hasCalculatedUsage = usageKeysCount > 0;
  const combinedTotalValue = !hasBulkSummary && hasCalculatedUsage
    ? (Array.isArray(displayHierarchy)
        ? displayHierarchy.reduce((sum, root) => sum + sumLeafUsage(root), 0)
        : 0)
    : NaN;
  const unitForCombined = Array.isArray(displayHierarchy) && displayHierarchy.some((d) => d?.deviceType === 'electrical_meter')
    ? 'kWh'
    : 'L';
  const labelTextColor = theme.palette.mode === 'dark' ? '#fff' : 'rgba(0, 0, 0, 0.8)';

  const renderBulkSummaryChart = (bulkNode) => {
    const bulkUsageValue = Number(deviceUsage?.[bulkNode.id]);
    const hasChildren = Array.isArray(bulkNode.children) && bulkNode.children.length > 0;
    const siteSumValue = hasChildren ? sumLeafUsage(bulkNode) : 0;
    const safeSiteSum = Number.isFinite(siteSumValue) ? siteSumValue : 0;
    const safeBulk = Number.isFinite(bulkUsageValue) ? bulkUsageValue : 0;
    const baseSiteBar = Math.min(safeBulk, safeSiteSum);
    const deltaAbs = Math.abs(safeBulk - safeSiteSum);
    const isLoss = safeSiteSum < safeBulk;
    const unit = bulkNode?.deviceType === 'electrical_meter' ? 'kWh' : 'L';
    const bulkLabel = bulkNode?.serialNumber
      ? `S/N ${bulkNode.serialNumber}${bulkNode.description ? ` - ${bulkNode.description}` : ''}`
      : (bulkNode?.description || String(bulkNode?.id || 'Bulk'));

    return (
      <Grid item xs={12} md={6} key={String(bulkNode.id)}>
        <Box sx={{ height: 220, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, textAlign: 'center', color: labelTextColor }}>
            {bulkLabel}
          </Typography>
          <Chart
            type="bar"
            plugins={[barSegmentValueLabelsPlugin]}
            data={{
              labels: ['Bulk', 'Children'],
              datasets: [
                {
                  label: `Value (${unit})`,
                  data: [safeBulk, baseSiteBar],
                  backgroundColor: ['rgba(33, 150, 243, 0.55)', 'rgba(33, 150, 243, 0.35)'],
                  borderColor: ['#1976d2', '#1976d2'],
                  borderWidth: 1,
                  stack: 'balance'
                },
                {
                  label: isLoss ? 'Loss' : 'Gain',
                  data: [0, deltaAbs],
                  backgroundColor: [
                    'rgba(0,0,0,0)',
                    isLoss ? 'rgba(244, 67, 54, 0.75)' : 'rgba(76, 175, 80, 0.75)'
                  ],
                  borderColor: [
                    'rgba(0,0,0,0)',
                    isLoss ? 'rgba(244, 67, 54, 1)' : 'rgba(76, 175, 80, 1)'
                  ],
                  borderWidth: 1,
                  stack: 'balance'
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'top' },
                datalabels: false,
                tooltip: {
                  callbacks: {
                    afterBody: () => {
                      const diff = safeBulk - safeSiteSum;
                      const diffLabel = Number.isFinite(diff) ? formatUsageValue(diff) : '-';
                      return [`Delta: ${diffLabel} ${unit}`];
                    }
                  }
                },
                barValueLabels: false,
                barValueLabelsV2: false,
                barValueLabelsV3: false,
                barValueLabelsV4: false,
                barSegmentValueLabels: false,
                barSegmentValueLabelsV2: {
                  display: true,
                  color: labelTextColor,
                  insideColor: labelTextColor,
                  fontSize: 10,
                  minValue: 0.01,
                  positionByDatasetIndex: { 0: 'middle', 1: 'insideTop' },
                  formatter: (v) => {
                    const num = Number(v);
                    if (!Number.isFinite(num) || num === 0) return '';
                    return Math.round(num).toLocaleString('en-US');
                  }
                }
              },
              scales: {
                x: { stacked: true },
                y: { stacked: true, beginAtZero: true }
              }
            }}
          />
        </Box>
      </Grid>
    );
  };

  const renderUnallocatedChart = () => {
    const unit = unallocatedRoots.some((d) => d?.deviceType === 'electrical_meter') ? 'kWh' : 'L';
    const safeUnallocated = Number.isFinite(unallocatedTotal) ? unallocatedTotal : 0;

    return (
      <Grid item xs={12} md={6} key="unallocated">
        <Box sx={{ height: 220, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, textAlign: 'center', color: labelTextColor }}>
            Unallocated
          </Typography>
          <Chart
            type="bar"
            plugins={[barSegmentValueLabelsPlugin]}
            data={{
              labels: ['Unallocated'],
              datasets: [
                {
                  label: `Value (${unit})`,
                  data: [safeUnallocated],
                  backgroundColor: ['rgba(255, 152, 0, 0.75)'],
                  borderColor: ['#f57c00'],
                  borderWidth: 1
                }
              ]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'top' },
                datalabels: false,
                tooltip: {
                  callbacks: {
                    afterBody: () => {
                      return [`Total: ${formatUsageValue(safeUnallocated)} ${unit}`];
                    }
                  }
                },
                barValueLabels: false,
                barValueLabelsV2: false,
                barValueLabelsV3: false,
                barValueLabelsV4: false,
                barSegmentValueLabels: false,
                barSegmentValueLabelsV2: {
                  display: true,
                  color: labelTextColor,
                  insideColor: labelTextColor,
                  fontSize: 10,
                  minValue: 0.01,
                  positionByDatasetIndex: { 0: 'middle' },
                  formatter: (v) => {
                    const num = Number(v);
                    if (!Number.isFinite(num) || num === 0) return '';
                    return Math.round(num).toLocaleString('en-US');
                  }
                }
              },
              scales: {
                x: { stacked: true },
                y: { stacked: true, beginAtZero: true }
              }
            }}
          />
        </Box>
      </Grid>
    );
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', overflow: 'auto' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Device Hierarchy</Typography>
        <Divider sx={{ mb: 2 }} />
        
        {/* Date Range Selector */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="From"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="To"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                onClick={calculateUsage}
                disabled={calculating}
                fullWidth
                startIcon={calculating ? <CircularProgress size={20} /> : null}
              >
                {calculating ? 'Calculating...' : 'Calculate Usage'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleExportHierarchyXlsx}
                disabled={calculating || !displayHierarchy || displayHierarchy.length === 0}
                fullWidth
              >
                Export Excel
              </Button>
              <Button
                variant="outlined"
                onClick={handleExportHierarchyCsv}
                disabled={calculating || !displayHierarchy || displayHierarchy.length === 0}
                fullWidth
              >
                Export CSV
              </Button>
            </Box>
          </Grid>
        </Grid>

        {(hasBulkSummary || hasUnallocated) && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {bulkNodes.map((bulkNode) => renderBulkSummaryChart(bulkNode))}
            {hasUnallocated && renderUnallocatedChart()}
          </Grid>
        )}
        
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!hasBulkSummary && hasCalculatedUsage && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Card
              variant="outlined"
              sx={{
                p: 1,
                width: { xs: '100%', sm: 280 },
                borderColor: unitForCombined === 'kWh' ? 'rgba(255, 152, 0, 0.6)' : 'rgba(33, 150, 243, 0.6)',
                backgroundColor: unitForCombined === 'kWh' ? 'rgba(255, 152, 0, 0.06)' : 'rgba(33, 150, 243, 0.06)'
              }}
            >
              <Typography variant="caption" color="text.secondary">Total Usage</Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {formatUsageValue(Number.isFinite(combinedTotalValue) ? combinedTotalValue : 0)} {unitForCombined}
              </Typography>
            </Card>
          </Box>
        )}
        
        <List sx={{ p: 0 }}>
          {displayHierarchy.map(device => renderDevice(device))}
        </List>
      </CardContent>
    </Card>
  );
};

export default DeviceFlowChart;
